import { memo } from 'react'
import { usePress } from '@react-aria/interactions'
import cn from 'classnames'

import { Text } from '~/shared/ui/Text'

import type { TextLinkProps } from './types'

import styles from './TextLink.module.scss'

/**
 * Текстовая ссылка — вход в раздел без карточки и без строки с шевроном.
 *
 * Нужна там, где вход по смыслу принадлежит соседнему тексту и не заслуживает собственного
 * блока: карточка в этом месте занимала бы место и читалась как «ещё один раздел».
 *
 * Подчёркивание — единственный признак кликабельности: цветом в продукте выделено многое,
 * и без подчёркивания нажимаемую строку от подписи не отличить. Синих ссылок нет
 * (docs/design-system.md §4), поэтому подчёркивание рисуем сами, тоном тише самого текста.
 *
 * Зона нажатия — 44×44 (правило 12): строка в 11.5–15px сама по себе ниже, поэтому её
 * добирает псевдоэлемент, а не паддинг — иначе ссылка отжимала бы соседние строки.
 */
export const TextLink = memo(function TextLink(props: TextLinkProps) {
  const { text, variant = 'BODY_S', color = 'ACCENT', ariaLabel, onPress } = props

  const { pressProps, isPressed } = usePress({ onPress })

  return (
    <span
      className={cn(styles.root, styles[`color_${color}`])}
      role='button'
      tabIndex={0}
      aria-label={ariaLabel}
      data-pressed={isPressed}
      {...pressProps}
    >
      <Text variant={variant} color={color}>
        {text}
      </Text>
    </span>
  )
})
